export type Listener<T = unknown> = (detail: T) => void;

export type SdkSignal =
  | 'ready'
  | 'error'
  | 'progress'
  | 'session-ended'
  | 'user-changed'
  | 'mute-changed'
  | 'ad-started'
  | 'ad-finished'
  | 'room-join';

export class Emitter {
  private readonly listeners = new Map<string, Set<Listener<never>>>();

  on<T>(signal: SdkSignal | string, handler: Listener<T>): () => void {
    if (typeof handler !== 'function') return () => {};
    const set = this.listeners.get(signal) ?? new Set();
    set.add(handler as Listener<never>);
    this.listeners.set(signal, set);
    return () => this.off(signal, handler);
  }

  off<T>(signal: SdkSignal | string, handler: Listener<T>): void {
    this.listeners.get(signal)?.delete(handler as Listener<never>);
  }

  /** A throwing listener must not stop the others or the caller. */
  emit<T>(signal: SdkSignal | string, detail: T): void {
    for (const handler of [...(this.listeners.get(signal) ?? [])]) {
      try {
        (handler as Listener<T>)(detail);
      } catch (error) {
        reportListenerFailure(signal, error);
      }
    }
    dispatchDomEvent(signal, detail);
  }

  clear(): void {
    this.listeners.clear();
  }
}

function reportListenerFailure(signal: string, error: unknown) {
  if (typeof console !== 'undefined' && console.error) {
    console.error(`[RizGames] listener for "${signal}" threw`, error);
  }
}

function dispatchDomEvent<T>(signal: string, detail: T) {
  if (typeof window === 'undefined' || typeof CustomEvent !== 'function') return;
  try {
    window.dispatchEvent(new CustomEvent(`riz:${signal}`, { detail }));
  } catch {
    // A document that forbids synthetic events is not a reason to fail a call.
  }
}
